/**
 * Kundevisning: SUPERADMIN kan agere alle firmaer; SALESPERSON kun egne firmaer.
 * Valget persisteres i sessionStorage så refresh ikke smider sælgeren ud af kundevisning.
 * Serversiden re-validerer altid ved ordreafgivelse.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/features/auth/AuthContext";
import { logger } from "@/lib/logger";

export interface ImpersonatedCompany {
  id: string;
  name: string;
  default_discount_pct: number | null;
}

interface ImpersonationState {
  company: ImpersonatedCompany | null;
  isImpersonating: boolean;
  startImpersonation: (companyId: string) => Promise<{ ok: boolean; error?: string }>;
  stopImpersonation: () => void;
}

const ImpersonationContext = createContext<ImpersonationState | undefined>(undefined);
const STORAGE_KEY = "lab3d_impersonation_company_id";

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const { user, isSuperAdmin, isSalesperson } = useAuth();
  const [company, setCompany] = useState<ImpersonatedCompany | null>(null);

  const startImpersonation = async (companyId: string) => {
    if (!user || !isSalesperson) return { ok: false, error: "Ingen adgang" };
    let query = supabase
      .from("companies")
      .select("id, name, default_discount_pct, salesperson_user_id")
      .eq("id", companyId);
    if (!isSuperAdmin) query = query.eq("salesperson_user_id", user.id);
    const { data, error } = await query.maybeSingle();
    if (error || !data) {
      logger.warn("impersonation_denied", { companyId, error: error?.message });
      return { ok: false, error: "Firmaet blev ikke fundet, eller du har ikke adgang til det." };
    }
    setCompany({ id: data.id, name: data.name, default_discount_pct: data.default_discount_pct });
    sessionStorage.setItem(STORAGE_KEY, data.id);
    return { ok: true };
  };

  const stopImpersonation = () => {
    setCompany(null);
    sessionStorage.removeItem(STORAGE_KEY);
  };

  // Genskab kundevisning efter refresh
  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved && !company && user && isSalesperson) {
      void startImpersonation(saved);
    }
    if (!user) setCompany(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isSalesperson]);

  return (
    <ImpersonationContext.Provider
      value={{ company, isImpersonating: company != null, startImpersonation, stopImpersonation }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation(): ImpersonationState {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) throw new Error("useImpersonation skal bruges inden for ImpersonationProvider");
  return ctx;
}

/** Returnerer det firma-id al data skal hentes for (kundevisning > eget firma). */
export function useActiveCompanyId(): string | null {
  const { profile } = useAuth();
  const { company } = useImpersonation();
  return company?.id ?? profile?.company_id ?? null;
}
